import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, MoreVertical, Plus } from "lucide-react";
import {
    Avatar,
    Callout,
    Input,
    Menu,
    MenuItem,
    Pill,
    Table,
    TableHead,
    TableRow,
    VbButton,
    VbEmpty,
    VbLoading,
} from "@/components/vb";
import ConfirmDialog from "@/components/settings/components/ConfirmDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import {
    cancelWorkspaceInvitation,
    inviteWorkspaceMember,
    removeWorkspaceMember,
} from "@/services/workspaceService";
import { WorkspaceInvitation, WorkspaceMember, WorkspaceRole } from "@/types/workspace";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COLUMNS = "36px minmax(0,1.5fr) minmax(0,1.8fr) minmax(0,1fr) minmax(0,1fr) 36px";

const formatMemberName = (member: WorkspaceMember): string => {
    const fullName = `${member.user.first_name ?? ""} ${member.user.last_name ?? ""}`.trim();
    return fullName || member.user.email;
};

const relativeFrom = (isoDate: string): string => {
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return "—";
    const days = Math.floor((Date.now() - parsed.getTime()) / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    return parsed.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

/** The API reports failures as prose; map the ones we can recognise onto copy
 *  that says what to do about them. Preserved from the pre-AITS-494 panel. */
const buildInviteErrorMessage = (errorMessage: string): string => {
    const normalized = errorMessage.toLowerCase();
    if (normalized.includes("member_limit") || normalized.includes("limit")) {
        return "Member limit reached for this billing account. Remove a member or upgrade your plan.";
    }
    if (normalized.includes("already") && normalized.includes("member")) {
        return "This user is already a workspace member.";
    }
    if (normalized.includes("invitation") && normalized.includes("pending")) {
        return "An invitation for this email is already pending.";
    }
    return errorMessage;
};

const ROLE_CAPABILITIES: { role: "owner" | "member"; label: string; items: string[] }[] = [
    {
        role: "owner",
        label: "Owner",
        items: [
            "Full control of the workspace",
            "Adds & removes companies",
            "Invites and removes members",
            "Manages billing, credits & payments",
            "Runs reports and Virtual Buyers",
        ],
    },
    {
        role: "member",
        label: "Member",
        items: [
            "Views reports for all companies",
            "Runs new reports",
            "Runs Virtual Buyer simulations",
            "Manages report schedules",
            "No access to billing or member management",
        ],
    },
];

const SettingsMembers: React.FC = () => {
    const { activeWorkspace } = useWorkspace();
    const { user } = useAuth();
    const { isWorkspaceOwner } = useWorkspaceRole();
    const {
        sortedMembers,
        pendingInvitations,
        loading,
        loadMembers,
        loadInvitations,
        memberLimit,
        seatsUsed,
        isMemberLimitReached,
        seatPct,
        seatBarClass,
        canReadSeats,
    } = useWorkspaceMembers();

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteLoading, setInviteLoading] = useState(false);

    const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(null);
    const [removingMember, setRemovingMember] = useState(false);

    const [invitationToCancel, setInvitationToCancel] = useState<WorkspaceInvitation | null>(null);
    const [cancellingInvitation, setCancellingInvitation] = useState(false);

    const isValidEmail = useMemo(() => EMAIL_RE.test(inviteEmail.trim()), [inviteEmail]);

    const handleInviteMember = async (): Promise<void> => {
        if (!activeWorkspace || !isValidEmail || inviteLoading) {
            return;
        }
        try {
            setInviteLoading(true);
            const response = await inviteWorkspaceMember(
                activeWorkspace.id,
                inviteEmail.trim().toLowerCase(),
            );
            toast.success(
                response.type === "member"
                    ? "User was added to workspace"
                    : "Invitation sent and marked pending",
            );
            setInviteEmail("");
            setInviteOpen(false);
            await loadMembers();
            await loadInvitations();
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to invite member";
            toast.error(buildInviteErrorMessage(message));
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRemoveMember = async (): Promise<void> => {
        if (!activeWorkspace || !memberToRemove) {
            return;
        }
        if (memberToRemove.role === WorkspaceRole.Owner) {
            toast.error("Workspace owner cannot be removed");
            setMemberToRemove(null);
            return;
        }
        try {
            setRemovingMember(true);
            await removeWorkspaceMember(activeWorkspace.id, memberToRemove.user.id);
            toast.success("Member removed");
            setMemberToRemove(null);
            await loadMembers();
        } catch {
            toast.error("Failed to remove member");
        } finally {
            setRemovingMember(false);
        }
    };

    const handleCancelInvitation = async (): Promise<void> => {
        if (!activeWorkspace || !invitationToCancel) {
            return;
        }
        try {
            setCancellingInvitation(true);
            await cancelWorkspaceInvitation(activeWorkspace.id, invitationToCancel.id);
            toast.success("Invitation cancelled");
            setInvitationToCancel(null);
            await loadInvitations();
        } catch {
            toast.error("Failed to cancel invitation");
        } finally {
            setCancellingInvitation(false);
        }
    };

    return (
        <>
            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Workspace members</h2>
                        <p className="vb-card-hd__sub">
                            Two roles. The <strong>Owner</strong> has full control;{" "}
                            <strong>Members</strong> use the product but can't manage billing or
                            people.
                        </p>
                    </div>
                    {isWorkspaceOwner && (
                        <VbButton
                            type="button"
                            onClick={() => setInviteOpen((open) => !open)}
                            disabled={isMemberLimitReached}
                            title={
                                isMemberLimitReached
                                    ? `All ${memberLimit} seats are in use`
                                    : undefined
                            }
                            aria-expanded={inviteOpen}
                        >
                            <Plus className="h-[13px] w-[13px]" aria-hidden="true" />
                            Invite member
                        </VbButton>
                    )}
                </div>

                {canReadSeats && isMemberLimitReached && (
                    <Callout tone="warn" icon={<AlertTriangle className="h-[15px] w-[15px]" aria-hidden="true" />}>
                        You've reached the <strong>{memberLimit}-seat</strong> limit for this
                        workspace. Remove a member or cancel a pending invite to free one up.
                    </Callout>
                )}

                {isWorkspaceOwner && inviteOpen && (
                    <div className="vb-dashed">
                        <h3 className="vb-dashed__h">Invite a new member</h3>
                        <div className="vb-dashed__row">
                            <Input
                                type="email"
                                value={inviteEmail}
                                onChange={(event) => setInviteEmail(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        void handleInviteMember();
                                    }
                                }}
                                placeholder="name@company.com"
                                aria-label="Invitee email"
                                disabled={inviteLoading}
                            />
                            <VbButton
                                type="button"
                                onClick={() => void handleInviteMember()}
                                disabled={!isValidEmail || inviteLoading}
                            >
                                {inviteLoading ? "Sending…" : "Send invite"}
                            </VbButton>
                        </div>
                        <p className="vb-dashed__help">
                            Existing users are added immediately. New users stay pending until they
                            sign up. Everyone joins as a Member.
                        </p>
                    </div>
                )}

                {canReadSeats && (
                <div className="vb-seat-meter">
                    <div>
                        <div className="vb-seat-meter__v">
                            {seatsUsed} of {memberLimit} seats used
                        </div>
                        <div className="vb-seat-meter__l">
                            {sortedMembers.length}{" "}
                            {sortedMembers.length === 1 ? "member" : "members"}
                            {pendingInvitations.length > 0
                                ? ` · ${pendingInvitations.length} ${
                                      pendingInvitations.length === 1 ? "invite" : "invites"
                                  } pending`
                                : ""}
                        </div>
                    </div>
                    <div
                        className={seatBarClass}
                        role="meter"
                        aria-valuenow={seatsUsed}
                        aria-valuemin={0}
                        aria-valuemax={memberLimit}
                        aria-label="Seats used"
                    >
                        <span style={{ width: `${seatPct}%` }} />
                    </div>
                </div>
                )}

                <Table columns={COLUMNS} minWidth={560} tight framed ariaLabel="Workspace members">
                    <TableHead>
                        <span />
                        <span>Name</span>
                        <span>Email</span>
                        <span>Role</span>
                        <span>Joined</span>
                        <span />
                    </TableHead>

                    {sortedMembers.map((member) => {
                        const name = formatMemberName(member);
                        const isOwner = member.role === WorkspaceRole.Owner;
                        const isSelf = user?.id === member.user.id;
                        return (
                            <TableRow key={member.id}>
                                {/* The dark tile marks YOU, not the owner — a
                                    member viewing the roster should see their
                                    own row highlighted (mock `.av.you`). */}
                                <Avatar
                                    size="md"
                                    round
                                    kind="user"
                                    name={name}
                                    decorative
                                />
                                <span>
                                    <span className="vb-dt-name">
                                        {name}
                                        {isSelf && <span className="vb-badge-you">You</span>}
                                        {isOwner && (
                                            <span
                                                className="vb-badge-locked"
                                                title="The owner can't be removed or demoted"
                                            >
                                                Locked
                                            </span>
                                        )}
                                    </span>
                                </span>
                                <span className="vb-dt-last" data-label="Email">
                                    {member.user.email}
                                </span>
                                <span data-label="Role">
                                    <Pill tone={isOwner ? "owner" : "member"}>
                                        {isOwner ? "Owner" : "Member"}
                                    </Pill>
                                </span>
                                <span className="vb-dt-last" data-label="Joined">
                                    {relativeFrom(member.joined_at)}
                                </span>
                                <span>
                                    {isWorkspaceOwner && !isOwner && (
                                        <Menu
                                            align="end"
                                            trigger={
                                                <button
                                                    type="button"
                                                    className="vb-dt-more"
                                                    aria-label={`Row actions for ${name}`}
                                                >
                                                    <MoreVertical
                                                        className="h-[14px] w-[14px]"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            }
                                        >
                                            <MenuItem
                                                danger
                                                onSelect={() => setMemberToRemove(member)}
                                            >
                                                Remove from workspace
                                            </MenuItem>
                                        </Menu>
                                    )}
                                </span>
                            </TableRow>
                        );
                    })}

                    {pendingInvitations.map((invitation) => (
                        <TableRow key={`invite-${invitation.id}`} className="vb-table__row--pending">
                            <Avatar size="md" round kind="user" name={invitation.email} decorative />
                            <span>
                                <span className="vb-dt-name">{invitation.email}</span>
                                <span className="vb-dt-meta">
                                    Invite sent {relativeFrom(invitation.created_at)}
                                </span>
                            </span>
                            <span className="vb-dt-last vb-dt-last--quiet" data-label="Email">
                                Not accepted yet
                            </span>
                            <span data-label="Role">
                                <Pill tone="pending">Pending</Pill>
                            </span>
                            <span className="vb-dt-last" data-label="Joined">
                                —
                            </span>
                            <span>
                                {isWorkspaceOwner && (
                                <Menu
                                    align="end"
                                    trigger={
                                        <button
                                            type="button"
                                            className="vb-dt-more"
                                            aria-label={`Row actions for ${invitation.email}`}
                                        >
                                            <MoreVertical
                                                className="h-[14px] w-[14px]"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    }
                                >
                                    <MenuItem
                                        danger
                                        onSelect={() => setInvitationToCancel(invitation)}
                                    >
                                        Cancel invite
                                    </MenuItem>
                                </Menu>
                                )}
                            </span>
                        </TableRow>
                    ))}
                </Table>

                {loading && sortedMembers.length === 0 && <VbLoading rows={3} />}
                {!loading && sortedMembers.length === 0 && (
                    <VbEmpty title="No members yet" description="Invite someone to share this workspace." />
                )}
            </section>

            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>What each role can do</h2>
                        <p className="vb-card-hd__sub">
                            Pick the smaller one unless someone needs to manage billing or people.
                        </p>
                    </div>
                </div>
                <div className="vb-rolegrid">
                    {ROLE_CAPABILITIES.map((role) => (
                        <div className="vb-rolecard" key={role.role}>
                            <div>
                                <Pill tone={role.role}>{role.label}</Pill>
                            </div>
                            <ul>
                                {role.items.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <ConfirmDialog
                open={memberToRemove !== null}
                onOpenChange={(open) => {
                    if (!open) setMemberToRemove(null);
                }}
                title="Remove member"
                description={
                    <>
                        Remove <strong>{memberToRemove ? formatMemberName(memberToRemove) : ""}</strong>{" "}
                        from {activeWorkspace?.name}? They'll lose access to its companies and
                        reports.
                    </>
                }
                confirmLabel="Remove member"
                pendingLabel="Removing…"
                destructive
                pending={removingMember}
                onConfirm={() => void handleRemoveMember()}
            />

            <ConfirmDialog
                open={invitationToCancel !== null}
                onOpenChange={(open) => {
                    if (!open) setInvitationToCancel(null);
                }}
                title="Cancel invitation"
                description={
                    <>
                        Cancel the pending invitation for{" "}
                        <strong>{invitationToCancel?.email}</strong>? It frees up the seat.
                    </>
                }
                confirmLabel="Cancel invitation"
                pendingLabel="Cancelling…"
                cancelLabel="Keep invitation"
                destructive
                pending={cancellingInvitation}
                onConfirm={() => void handleCancelInvitation()}
            />
        </>
    );
};

export default SettingsMembers;
